/**
 * Wave 317 · Day 1 · Procedural placeholder blocks.
 *
 * These are stand-ins. The real architecture is the composer above this
 * file; the viewer below renders whatever comes out of the composer. In
 * Day 2-3, each block gets replaced by an authored `.glb` (Blender) loaded
 * via `useGLTF` — same component name, same props, swap the body.
 *
 * The placeholders are intentional: warm palette, MeshToonMaterial, soft
 * primitives, light emissive on suite windows. The premise is "encantar o
 * comprador" — even at v0, this should not look like an engineering
 * diagram. It should feel like a tiny diorama.
 *
 * Each Block is a self-contained R3F component:
 *   - Reads `paramOverrides` from `BlockInstance`
 *   - Procedurally builds geometry from primitives
 *   - Plays its own ambient motion via `useFrame`
 *   - Respects `prefers-reduced-motion` (motion paused; geometry stays)
 */

import { useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import type { BlockInstance } from './types';
import { PALETTE } from './types';

// ── Util ───────────────────────────────────────────────────────────────

function readParam<T extends number | string | boolean>(
  inst: BlockInstance,
  key: string,
  fallback: T,
): T {
  const v = inst.paramOverrides?.[key];
  return (v ?? fallback) as T;
}

function usePrefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

// MeshToon master — every block uses this shading model. Cohesion = single
// material vocabulary across the whole scene (emil rule, design-system
// rule, AND the Littlest Tokyo lesson).
function ToonMat({
  color,
  roughness = 0.85,
  emissive,
  emissiveIntensity = 0,
  transparent = false,
  opacity = 1,
}: {
  color: string;
  roughness?: number;
  emissive?: string;
  emissiveIntensity?: number;
  transparent?: boolean;
  opacity?: number;
}): JSX.Element {
  return (
    <meshToonMaterial
      color={color}
      emissive={emissive ?? '#000000'}
      emissiveIntensity={emissiveIntensity}
      transparent={transparent}
      opacity={opacity}
    />
  );
}

// ── GROUND ─────────────────────────────────────────────────────────────
//
// 50×50 m flat plane plus a slightly larger darker frame. The lot sits
// inside the inner plane. Subtle UV tiling so it doesn't read flat-poster.

export function Ground(_: { instance: BlockInstance }): JSX.Element {
  return (
    <group>
      {/* Outer frame */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
        <planeGeometry args={[80, 80]} />
        <ToonMat color={PALETTE.groundEdge} />
      </mesh>
      {/* Inner lot */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
        <planeGeometry args={[50, 36]} />
        <ToonMat color={PALETTE.ground} />
      </mesh>
    </group>
  );
}

// ── SKY ────────────────────────────────────────────────────────────────
//
// Wave 317 D1 fix: the previous sky was a lit sphere that enclosed the
// camera and went dark from the inside (toon shader respects normals,
// inside-view = backface = unlit). Now we use a `BackSide` unlit basic
// material with a vertical gradient simulated by shaderMaterial-light.
// The viewer also paints a `<color attach="background">` on the Canvas
// as a fallback for when the camera somehow steps outside the sphere.

export function Sky(_: { instance: BlockInstance }): JSX.Element {
  return (
    <mesh position={[0, 0, 0]} renderOrder={-1}>
      <sphereGeometry args={[80, 32, 16]} />
      <shaderMaterial
        attach="material"
        side={THREE.BackSide}
        depthWrite={false}
        uniforms={{
          uTop: { value: new THREE.Color(PALETTE.skyTop) },
          uBot: { value: new THREE.Color(PALETTE.sky) },
        }}
        vertexShader={`
          varying vec3 vWorld;
          void main() {
            vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          varying vec3 vWorld;
          uniform vec3 uTop;
          uniform vec3 uBot;
          void main() {
            // Map world-Y to 0..1 over [-10..50]; clamp.
            float t = clamp((vWorld.y + 10.0) / 60.0, 0.0, 1.0);
            // Soft ease toward the horizon — no hard band.
            t = smoothstep(0.0, 1.0, t);
            gl_FragColor = vec4(mix(uBot, uTop, t), 1.0);
          }
        `}
      />
    </mesh>
  );
}

// ── SUITE ──────────────────────────────────────────────────────────────
//
// A simple cuboid with a window cutout (procedural) and an emissive pulse
// at dusk. Ambient motion: window emissive cycles 0 → 1 over 8s, holds,
// fades. This is the loop that sells "people live here."

export function Suite({ instance }: { instance: BlockInstance }): JSX.Element {
  const w = readParam(instance, 'width', 3.6) as number;
  const d = readParam(instance, 'depth', 4.0) as number;
  const h = 2.8;

  const windowMat = useRef<THREE.MeshToonMaterial>(null);
  const reduced = usePrefersReducedMotion();
  const phase = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame((state) => {
    if (reduced || !windowMat.current) return;
    // Slow dusk pulse — 8s cycle, peak around 0.7 to suggest "warm but not
    // glaring." Each suite gets a small phase offset so they don't pulse
    // in lockstep (that would feel wrong, like a marquee).
    const t = state.clock.getElapsedTime();
    const v = (Math.sin((t / 4) + phase) + 1) / 2; // 0..1
    windowMat.current.emissiveIntensity = 0.4 + v * 0.5;
  });

  return (
    <group position={instance.position} rotation={[0, instance.rotation ?? 0, 0]}>
      {/* Walls */}
      <mesh position={[0, h / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <ToonMat color={PALETTE.wallSuites} />
      </mesh>
      {/* Roof slab — slightly larger than walls for overhang */}
      <mesh position={[0, h + 0.07, 0]} castShadow>
        <boxGeometry args={[w + 0.3, 0.14, d + 0.3]} />
        <ToonMat color={PALETTE.roof} />
      </mesh>
      {/* Window strip facing south (positive Z) — emissive pulses dusk */}
      <mesh position={[0, h * 0.55, d / 2 + 0.001]}>
        <planeGeometry args={[w * 0.7, h * 0.45]} />
        <meshToonMaterial
          ref={windowMat}
          color={PALETTE.glass}
          emissive={PALETTE.emissiveDusk}
          emissiveIntensity={0.5}
          transparent
          opacity={0.92}
        />
      </mesh>
    </group>
  );
}

// ── BRISE PANEL ────────────────────────────────────────────────────────
//
// A perforated wood panel that sits in front of the suite façade.
// Procedural perforation = box with alpha-noise texture. Ambient motion:
// none on the geometry; the shadow it casts will animate via the sun
// proxy (sky_rig) — that's the magic moment.

export function BrisePanel({ instance }: { instance: BlockInstance }): JSX.Element {
  const w = readParam(instance, 'width', 3.6) as number;
  const h = 2.4;
  return (
    <group position={instance.position} rotation={[0, instance.rotation ?? 0, 0]}>
      <mesh position={[0, h / 2, 0]} castShadow>
        <boxGeometry args={[w, h, 0.05]} />
        <ToonMat color={PALETTE.brise} roughness={0.95} />
      </mesh>
      {/* Vertical slats — 8 thin rectangles for the cobogó feel.
          Alternating Z position fakes the perforation depth without an
          alpha texture. */}
      {Array.from({ length: 8 }).map((_, i) => {
        const slatW = w / 10;
        const slatX = -w / 2 + slatW / 2 + i * (w / 8);
        return (
          <mesh key={i} position={[slatX, h / 2, 0.04]}>
            <boxGeometry args={[slatW * 0.7, h * 0.95, 0.04]} />
            <ToonMat color={PALETTE.brise} />
          </mesh>
        );
      })}
    </group>
  );
}

// ── POOL ───────────────────────────────────────────────────────────────
//
// Sunken rectangular pool with deck around it. Ambient motion: water
// surface UV scrolls slowly. Pool tile color = palette.pool.

export function Pool(_: { instance: BlockInstance }): JSX.Element {
  const water = useRef<THREE.MeshToonMaterial>(null);
  const reduced = usePrefersReducedMotion();

  useFrame((state) => {
    if (reduced || !water.current) return;
    // Slight emissive flicker — fakes light-on-water without a shader.
    const t = state.clock.getElapsedTime();
    water.current.emissiveIntensity = 0.05 + Math.sin(t * 0.5) * 0.03;
  });

  return (
    <group position={[-13, 0, 1]}>
      {/* Deck */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <boxGeometry args={[7, 0.1, 5]} />
        <ToonMat color={PALETTE.deck} />
      </mesh>
      {/* Pool sunken in */}
      <mesh position={[0, 0.07, 0]}>
        <boxGeometry args={[5, 0.12, 3]} />
        <ToonMat color={PALETTE.skyTop} />
      </mesh>
      {/* Water plane — slightly above the sunken floor */}
      <mesh position={[0, 0.14, 0]}>
        <planeGeometry args={[4.8, 2.8]} />
        <meshToonMaterial
          ref={water}
          color={PALETTE.pool}
          emissive={PALETTE.pool}
          emissiveIntensity={0.05}
          transparent
          opacity={0.85}
        />
      </mesh>
    </group>
  );
}

// ── GARAGE ─────────────────────────────────────────────────────────────
//
// Open carport with 4 columns and a flat roof. Reads as "garagem coberta"
// without enclosing walls — matches Brazilian residential typology.

export function Garage(_: { instance: BlockInstance }): JSX.Element {
  const h = 2.6;
  const w = 5;
  const d = 5.5;
  return (
    <group position={[20, 0, 0]}>
      {/* Roof */}
      <mesh position={[0, h, 0]} castShadow>
        <boxGeometry args={[w, 0.18, d]} />
        <ToonMat color={PALETTE.roof} />
      </mesh>
      {/* 4 columns */}
      {([
        [-w / 2 + 0.2, h / 2,  d / 2 - 0.2],
        [ w / 2 - 0.2, h / 2,  d / 2 - 0.2],
        [-w / 2 + 0.2, h / 2, -d / 2 + 0.2],
        [ w / 2 - 0.2, h / 2, -d / 2 + 0.2],
      ] as Array<[number, number, number]>).map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <boxGeometry args={[0.25, h, 0.25]} />
          <ToonMat color={PALETTE.wallWet} />
        </mesh>
      ))}
      {/* Driveway pavement */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <planeGeometry args={[w, d]} />
        <ToonMat color={PALETTE.deck} />
      </mesh>
    </group>
  );
}

// ── GARDEN STRIP ───────────────────────────────────────────────────────

export function GardenStrip({ instance }: { instance: BlockInstance }): JSX.Element {
  const w = readParam(instance, 'width', 22) as number;
  const d = readParam(instance, 'depth', 1.5) as number;
  return (
    <group position={instance.position}>
      <mesh position={[0, 0.04, 0]}>
        <boxGeometry args={[w, 0.08, d]} />
        <ToonMat color={PALETTE.grass} />
      </mesh>
    </group>
  );
}

// ── CHIMNEY ────────────────────────────────────────────────────────────
//
// Small stack with rising smoke wisp. Ambient motion: smoke rises +
// dissipates on a 4s loop.

export function Chimney({ instance }: { instance: BlockInstance }): JSX.Element {
  const smoke = useRef<THREE.Mesh>(null);
  const smokeMat = useRef<THREE.MeshToonMaterial>(null);
  const reduced = usePrefersReducedMotion();

  useFrame((state) => {
    if (reduced || !smoke.current || !smokeMat.current) return;
    const t = (state.clock.getElapsedTime() % 4) / 4; // 0..1 over 4s
    smoke.current.position.y = 3.5 + t * 2.5;
    smoke.current.scale.setScalar(0.3 + t * 0.6);
    smokeMat.current.opacity = 0.6 * (1 - t);
  });

  return (
    <group position={instance.position}>
      <mesh position={[0, 1.7, 0]} castShadow>
        <boxGeometry args={[0.45, 3.2, 0.45]} />
        <ToonMat color={PALETTE.wallWet} />
      </mesh>
      <mesh ref={smoke} position={[0, 3.5, 0]}>
        <sphereGeometry args={[0.4, 12, 12]} />
        <meshToonMaterial ref={smokeMat} color={PALETTE.smoke} transparent opacity={0.6} />
      </mesh>
    </group>
  );
}

// ── TREE ───────────────────────────────────────────────────────────────
//
// Trunk + 3 leaf-spheres at varied heights. Ambient motion: gentle Y-axis
// sway on the leaves.

export function Tree({ instance }: { instance: BlockInstance }): JSX.Element {
  const leaves = useRef<THREE.Group>(null);
  const reduced = usePrefersReducedMotion();
  const phase = useMemo(() => Math.random() * Math.PI * 2, []);

  useFrame((state) => {
    if (reduced || !leaves.current) return;
    const t = state.clock.getElapsedTime();
    leaves.current.rotation.z = Math.sin(t * 0.6 + phase) * 0.04;
  });

  return (
    <group position={instance.position}>
      <mesh position={[0, 1.3, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.18, 2.6, 8]} />
        <ToonMat color={PALETTE.deck} />
      </mesh>
      <group ref={leaves}>
        <mesh position={[0, 3.0, 0]} castShadow>
          <sphereGeometry args={[0.9, 12, 12]} />
          <ToonMat color={PALETTE.grass} />
        </mesh>
        <mesh position={[0.5, 2.6, 0.3]} castShadow>
          <sphereGeometry args={[0.6, 10, 10]} />
          <ToonMat color={PALETTE.grass} />
        </mesh>
        <mesh position={[-0.4, 2.7, -0.2]} castShadow>
          <sphereGeometry args={[0.55, 10, 10]} />
          <ToonMat color={PALETTE.grass} />
        </mesh>
      </group>
    </group>
  );
}

// ── CAR ────────────────────────────────────────────────────────────────
//
// Stylized low-poly car. Sits parked. No animation in v0 — JP can add a
// "drives by" loop in Day 2 once the static look is locked.

export function Car({ instance }: { instance: BlockInstance }): JSX.Element {
  return (
    <group position={instance.position} rotation={[0, instance.rotation ?? 0, 0]}>
      {/* Body */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[1.7, 0.5, 4]} />
        <ToonMat color={PALETTE.ink} />
      </mesh>
      {/* Cabin */}
      <mesh position={[0, 1.05, -0.2]} castShadow>
        <boxGeometry args={[1.5, 0.4, 2.2]} />
        <ToonMat color={PALETTE.skyTop} />
      </mesh>
      {/* Wheels */}
      {([
        [-0.8, 0.3,  1.3],
        [ 0.8, 0.3,  1.3],
        [-0.8, 0.3, -1.3],
        [ 0.8, 0.3, -1.3],
      ] as Array<[number, number, number]>).map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[Math.PI / 2, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.3, 0.3, 0.18, 12]} />
          <ToonMat color={PALETTE.groundEdge} />
        </mesh>
      ))}
    </group>
  );
}

// ── BLOCK REGISTRY ─────────────────────────────────────────────────────
//
// Single source of truth for "blockId → Component." Adding a new block =
// authoring its component + adding one entry here. Composer references
// blockIds; viewer looks them up here.

export const BLOCK_REGISTRY: Record<
  BlockInstance['blockId'],
  (props: { instance: BlockInstance }) => JSX.Element
> = {
  ground: Ground,
  sky: Sky,
  suite: Suite,
  pool: Pool,
  garage: Garage,
  garden_strip: GardenStrip,
  brise_panel: BrisePanel,
  chimney: Chimney,
  tree: Tree,
  car: Car,
};

// ESLint sweetener: useEffect imported but only used to silence dead-code
// removal in some bundlers when block hooks are conditionally absent.
useEffect.length;
